/**
 * One I-tick of one player, as the C64's IRQ runs it: D_1E6C (probe the map, remember the frame in
 * $25, dispatch on the state) and the play state's handler D_2162, which blows, bounces, jumps, falls
 * or walks.
 */

import { addScore } from '../score';
import { fallTick } from './air';
import { blowStep } from './blow';
import { jumpTick, startJump } from './jump';
import { deathTick, spawnTimer } from './life';
import { probe, solidRun, type Probe } from './probe';
import { bubbleUnderFeet } from './push';
import { JOY_UP } from './state';
import { BOUNCE_SCORE } from './tuning';
import { groundTick } from './walk';
import type { Player, World } from '../types';

/** D_1E6C: the player's handler for its state ($0E dying, $0F dead: nothing, $10 invulnerable, 1 play). */
export function runPlayer(world: World, p: Player): void {
  const m = p.motion;
  const frame0 = m.frame;
  const pr = probe(p.x, p.y);
  if (p.state === 'dying') {
    if (!m.dead) deathTick(world, p);
    return;
  }
  if (p.state !== 'play') return;
  if (p.invulnerable > 0) spawnTimer(p);
  playerTick(world, p, pr, frame0);
}

/**
 * D_2162. The blow animation steps first. Then, not rising (standing, walking, falling, or past the
 * apex), holding UP over a floating bubble bounces. Then by state: in a jump (D_23EA), falling
 * (D_257C), or standing - where no floor under the feet (on the row grid) starts a fall this tick.
 */
function playerTick(world: World, p: Player, pr: Probe, frame0: number): void {
  const m = p.motion;
  if (m.blow >= 0) blowStep(world, p);
  if (m.jump <= 0 && (m.joy & JOY_UP) !== 0) {
    const b = bubbleUnderFeet(world, p);
    if (b) {
      addScore(world, p.id, BOUNCE_SCORE);
      restartJump(p, frame0);
      world.events.push({ type: 'bounce', player: p.id, bubbleId: b.id });
      return;
    }
  }
  if (m.jump >= 0) {
    jumpTick(world, p, pr, frame0);
    return;
  }
  if (!m.falling && pr.fy === 0 && !solidRun(world, pr, 0x51)) m.falling = true;
  if (m.falling) {
    fallTick(world, p, pr, frame0);
    return;
  }
  groundTick(world, p, pr, frame0);
}

/**
 * D_2162's bounce path after `jsr D_7C21`: the jump and the fall cleared, Y onto the odd (grid) parity,
 * then the takeoff (D_222B).
 */
export function restartJump(p: Player, frame0: number): void {
  const m = p.motion;
  m.jump = -1;
  m.descent = -1;
  m.falling = false;
  p.y |= 1;
  startJump(p, frame0);
}
